import { useEffect, useMemo, useRef, useState } from "react";
import { clampCrop, type SurfImageCrop } from "@vida/surf-core";
import { useCropDrag } from "../../hooks/useCropDrag";

type AspectPreset = {
  id: string;
  label: string;
  /** Target display aspect (width/height in pixels), or null for free. */
  ratio: number | null;
};

const PRESETS: AspectPreset[] = [
  { id: "free", label: "Libero", ratio: null },
  { id: "16:9", label: "16:9", ratio: 16 / 9 },
  { id: "4:3", label: "4:3", ratio: 4 / 3 },
  { id: "1:1", label: "1:1", ratio: 1 },
];

const FULL_CROP: SurfImageCrop = { x: 0, y: 0, w: 1, h: 1 };

type Props = {
  imageUrl: string;
  /** Existing crop (source fractions), or null for full image. */
  crop: SurfImageCrop | null;
  onConfirm: (crop: SurfImageCrop) => void;
  onReset: () => void;
  onCancel: () => void;
};

/**
 * Fit a crop to a target DISPLAY aspect ratio (pixels), keeping the center,
 * clamped into the unit square. `naturalAspect` = naturalWidth / naturalHeight.
 */
function fitCropToAspect(
  crop: SurfImageCrop,
  targetRatio: number,
  naturalAspect: number,
): SurfImageCrop {
  // display aspect = (w/h) * naturalAspect  ⇒  w/h = targetRatio / naturalAspect
  const wh = targetRatio / naturalAspect;
  const cx = crop.x + crop.w / 2;
  const cy = crop.y + crop.h / 2;
  // Start from current height, derive width; shrink if it overflows.
  let h = crop.h;
  let w = h * wh;
  if (w > 1) {
    w = 1;
    h = w / wh;
  }
  if (h > 1) {
    h = 1;
    w = h * wh;
  }
  let x = cx - w / 2;
  let y = cy - h / 2;
  x = Math.min(Math.max(0, x), 1 - w);
  y = Math.min(Math.max(0, y), 1 - h);
  return clampCrop({ x, y, w, h });
}

export function ImageCropOverlay({ imageUrl, crop, onConfirm, onReset, onCancel }: Props) {
  const frameRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState<SurfImageCrop>(() => (crop ? clampCrop(crop) : FULL_CROP));
  const [naturalAspect, setNaturalAspect] = useState<number | null>(null);
  const [presetId, setPresetId] = useState<string>("free");

  const { onPointerDown } = useCropDrag({
    containerRef: frameRef,
    crop: draft,
    onChange: setDraft,
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const applyPreset = (preset: AspectPreset) => {
    setPresetId(preset.id);
    if (preset.ratio == null || !naturalAspect) return;
    setDraft((cur) => fitCropToAspect(cur, preset.ratio!, naturalAspect));
  };

  const frameStyle = useMemo(
    () => ({
      aspectRatio: naturalAspect ? `${naturalAspect}` : "16 / 9",
    }),
    [naturalAspect],
  );

  const cropPct = {
    left: `${draft.x * 100}%`,
    top: `${draft.y * 100}%`,
    width: `${draft.w * 100}%`,
    height: `${draft.h * 100}%`,
  };

  return (
    <div className="image-crop-overlay" role="dialog" aria-label="Ritaglia immagine">
      <div className="image-crop-backdrop" onPointerDown={onCancel} />
      <div className="image-crop-panel">
        <div className="image-crop-stage">
          <div ref={frameRef} className="image-crop-frame" style={frameStyle}>
            <img
              src={imageUrl}
              alt=""
              draggable={false}
              className="image-crop-img"
              onLoad={(e) => {
                const el = e.currentTarget;
                if (el.naturalWidth > 0 && el.naturalHeight > 0) {
                  setNaturalAspect(el.naturalWidth / el.naturalHeight);
                }
              }}
            />
            {/* Darken outside the crop with 4 masks. */}
            <div className="image-crop-mask" style={{ left: 0, top: 0, width: "100%", height: cropPct.top }} />
            <div
              className="image-crop-mask"
              style={{ left: 0, top: cropPct.top, width: cropPct.left, height: cropPct.height }}
            />
            <div
              className="image-crop-mask"
              style={{
                left: `${(draft.x + draft.w) * 100}%`,
                top: cropPct.top,
                right: 0,
                height: cropPct.height,
              }}
            />
            <div
              className="image-crop-mask"
              style={{
                left: 0,
                top: `${(draft.y + draft.h) * 100}%`,
                width: "100%",
                bottom: 0,
              }}
            />
            <div className="image-crop-rect" style={cropPct} onPointerDown={onPointerDown("move")}>
              <span className="image-crop-handle nw" onPointerDown={onPointerDown("resize-nw")} />
              <span className="image-crop-handle ne" onPointerDown={onPointerDown("resize-ne")} />
              <span className="image-crop-handle sw" onPointerDown={onPointerDown("resize-sw")} />
              <span className="image-crop-handle se" onPointerDown={onPointerDown("resize-se")} />
            </div>
          </div>
        </div>
        <div className="image-crop-toolbar">
          <div className="image-crop-presets" role="group" aria-label="Proporzioni">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                className={presetId === p.id ? "surf-btn surf-btn--ghost active" : "surf-btn surf-btn--ghost"}
                onClick={() => applyPreset(p)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="image-crop-actions">
            <button type="button" className="surf-btn surf-btn--ghost" onClick={onReset}>
              Reset
            </button>
            <button type="button" className="surf-btn surf-btn--ghost" onClick={onCancel}>
              Annulla
            </button>
            <button
              type="button"
              className="primary-btn"
              onClick={() => onConfirm(clampCrop(draft))}
            >
              Conferma
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
